import i18next from "i18next";
import { LootWindow } from "./windows/loot-window";
import { PlayerWindow } from "./windows/player-window";
import { InventoryWindow } from "./windows/inventory-window";
import { MerchantWindow } from "./windows/merchant-window";
import { ActionSlot } from "./action-slot";
import { WeaponUI } from "./weapon-ui";
import { playSound } from "../utils/sound-utils";
import { ModernButton } from "./modern-button";
import { CosmeticsWindow } from "./windows/cosmetics-window";
import { resetDesktopKeyStates } from "../controls/controls-desktop";
import { TutorialPopup } from "./tutorial-popup";
import { NotificationHandler, NotificationType } from "./notification-handler";
import { DailyStarsComponent } from "./daily-stars-component";
import { DebugWindow } from "./windows/debug-window";
import { RanksWindow } from "../scenes/base/windows/ranks-window";
import { hideJoysticks, showJoysticks } from "../controls/controls-touch";
import {
  Item,
  ItemDefinition,
  LootBoxInfo,
  MerchantInfo,
  SlotType,
  SquadMemberDetail,
  SquadUpdateData,
} from "../models/common";
import { Player } from "../models/player";
import { Merchant } from "../models/merchant";

// Path to bleeding icon
const BLEEDING_ICON_KEY = "bleeding-icon";
const BLEEDING_ICON_PATH = "/sprites/icons/bleeding-icon.png";

export class WindowManager {
  private ranksButton: Phaser.GameObjects.Image | null = null;
  private scene: Phaser.Scene;
  private lootWindow: LootWindow;
  private openButton: ModernButton | null = null;
  private merchantButton: ModernButton | null = null;
  private uiBtnContainer: Phaser.GameObjects.Container | null = null;
  private inventoryBtn: Phaser.GameObjects.Image | null = null;
  private adminBtn: Phaser.GameObjects.Rectangle | null = null;
  private adminText: Phaser.GameObjects.Text | null = null;
  private playerWindow: PlayerWindow;
  private inventoryWindow: InventoryWindow;
  private merchantWindow: MerchantWindow;
  private cosmeticsWindow: CosmeticsWindow;
  private tutorialPopup: TutorialPopup;
  private ranksWindow: RanksWindow;
  public weaponUI: WeaponUI;
  private debugWindow: DebugWindow;

  private dailyStarsComponent: DailyStarsComponent | null = null;
  private actionSlotsContainer: Phaser.GameObjects.Container | null = null;
  public actionSlots: ActionSlot[] = [];
  private actionSlotsInteractive: boolean = true; // Track interactive state to avoid unnecessary calls
  // Sticky override so action slots/joysticks stay hidden through death (e.g. while the
  // kill message is showing), even though other events keep calling updateActionSlotsVisibility().
  private actionUIForceHidden: boolean = false;
  private bleedingIconObj: Phaser.GameObjects.Image | null = null;
  private closeWindowButton: ModernButton | null = null;
  private actionButtonsContainer: Phaser.GameObjects.Container | null = null; // For multiple loot action buttons
  private debugTapCount: number = 0;
  private debugTapTimer: ReturnType<typeof setTimeout> | null = null;

  private uiLayer: Phaser.GameObjects.Container;

  private notificationHandler: NotificationHandler;

  private ACTION_BUTTON_POS_Y: number = 0;
  private isBaseScene: boolean = false;

  constructor(scene: Phaser.Scene, uiLayer: Phaser.GameObjects.Container) {
    this.scene = scene;
    this.uiLayer = uiLayer;
    this.isBaseScene = scene.scene.key !== "MainScene";
    this.lootWindow = new LootWindow(scene, this, uiLayer);
    this.playerWindow = new PlayerWindow(scene, this, uiLayer);
    this.inventoryWindow = new InventoryWindow(scene, this, uiLayer);
    this.merchantWindow = new MerchantWindow(scene, this, uiLayer);
    this.cosmeticsWindow = new CosmeticsWindow(scene, uiLayer);
    this.tutorialPopup = new TutorialPopup(scene, this, uiLayer);
    this.ranksWindow = new RanksWindow(scene, this);
    this.ranksWindow.setUiLayer(uiLayer);
    this.weaponUI = new WeaponUI(scene, uiLayer);
    this.debugWindow = new DebugWindow(scene, uiLayer);
    this.bleedingIconObj = null;

    this.showActionSlots();

    this.ACTION_BUTTON_POS_Y = scene.scale.height - 120;

    this.notificationHandler = new NotificationHandler(this.scene, this.uiLayer);

    // Listen for language changes and update UI
    i18next.on("languageChanged", () => {
      this.updateButtonTexts();
    });
  }

  private updateButtonTexts() {
    // Update open button text
    if (this.openButton) {
      this.openButton.setText(this.t("ui:buttons.open", "OPEN"));
    }
    // Update merchant button text
    if (this.merchantButton) {
      this.merchantButton.setText(this.t("ui:buttons.trade", "TRADE"));
    }
  }

  /**
   * Safely get translated text, with fallback if i18next isn't ready
   */
  private t(key: string, fallback: string): string {
    try {
      if (i18next && i18next.t) {
        const result = i18next.t(key);
        // Check if result is a translation key (untranslated)
        if (result && result !== key && result.includes('[') === false) {
          console.log(`✓ Translation found: ${key} → ${result}`);
          return result;
        }
      }
    } catch (error) {
      console.warn(`Translation error for key ${key}:`, error);
    }
    console.log(`✗ Using fallback for ${key}: ${fallback}`);
    return fallback;
  }

  /**
   * Get translated text for button labels with special handling for common buttons
   */
  private getButtonText(label: string | undefined): string {
    if (!label) {
      return this.t("ui:buttons.open", "OPEN");
    }

    // Map common button labels to translation keys
    const labelMap: { [key: string]: string } = {
      "OPEN": "ui:buttons.open",
      "CRAFT": "ui:buttons.craft",
      "DRESS": "ui:buttons.change",
      "PICK UP / OPEN": "ui:buttons.pickUp",
      "DAILY SPIN": "ui:buttons.dailySpin",
    };

    const translationKey = labelMap[label];
    if (translationKey) {
      return this.t(translationKey, label);
    }

    // For any other label, just return it as-is
    return label;
  }

  static preload(scene: Phaser.Scene) {
    scene.load.image("inventory-icon", "/sprites/icons/backpack-icon.png");
    scene.load.image("belt-icon", "/sprites/icons/belt-icon.png");
    scene.load.image("player-icon", "/sprites/icons/player-icon.png");
    scene.load.image("quests-icon", "/sprites/icons/quests-icon.png");
    scene.load.image("ranks-icon", "/sprites/icons/ranks-icon.png");
    scene.load.image("profile-icon", "/sprites/icons/profile-icon.png");
    scene.load.image("squad-icon", "/sprites/icons/squad-icon.png");
    scene.load.image("squad-active-icon", "/sprites/icons/squad-active-icon.png");
    scene.load.image("settings-icon", "/sprites/icons/settings-icon.png");
    scene.load.image("shop-icon", "/sprites/icons/shop-icon.png");
    scene.load.image("storage-icon", "/sprites/icons/storage-icon.png");
    scene.load.image("raider-icon", "/sprites/icons/raider-icon.png");
    scene.load.image("workshop-icon", "/sprites/icons/workshop-icon.png");
    scene.load.image("weekly-icon", "/sprites/icons/weekly-icon.png");
    scene.load.image("star-filled", "/sprites/icons/star-filled.png");
    scene.load.image("star-empty", "/sprites/icons/star-empty.png");
    scene.load.image(BLEEDING_ICON_KEY, BLEEDING_ICON_PATH);
    TutorialPopup.preload(scene);

    // Season cosmetics — profile icons, hat skins, loot box tier icons
    scene.load.image("profile-icon-1", "/sprites/season/profile-icons/profile-1.png");
    scene.load.image("profile-icon-2", "/sprites/season/profile-icons/profile-2.png");
    scene.load.image("profile-icon-3", "/sprites/season/profile-icons/profile-3.png");
    scene.load.image("profile-icon-4", "/sprites/season/profile-icons/profile-4.png");
    scene.load.image("profile-icon-5", "/sprites/season/profile-icons/profile-5.png");
    scene.load.image("profile-icon-6", "/sprites/season/profile-icons/profile-6.png");
    scene.load.image("profile-icon-7", "/sprites/season/profile-icons/profile-7.png");
    scene.load.image("profile-icon-8", "/sprites/season/profile-icons/profile-8.png");
    scene.load.image("border-1", "/sprites/season/borders/border-1.png");
    scene.load.image("border-2", "/sprites/season/borders/border-2.png");
    scene.load.image("border-3", "/sprites/season/borders/border-3.png");
    scene.load.image("border-4", "/sprites/season/borders/border-4.png");
    for (let i = 1; i <= 16; i++) {
      scene.load.image(`hat-${i}`, `/sprites/season/hats/hat-${i}.png`);
    }
    // Chest tier icons — closed (first) frame of each tier's open animation, see LOOTBOX_FRAME in season-screen.ts
    scene.load.spritesheet("lootbox-chests", "/sprites/season/chests.png", { frameWidth: 32, frameHeight: 32 });
  }

  /**
   * Update the inventory icon in the UI (belt/backpack) based on current equipment
   */
  updateInventoryIcon() {
    if (!this.scene || !this.scene.sys || !this.scene.sys.textures) {
      console.warn(
        "WindowManager.updateInventoryIcon: scene, scene.sys, or scene.sys.textures is undefined",
      );
      return;
    }
    if (!this.uiBtnContainer) {
      return;
    }
    // Find inventory button in container (Phaser.GameObjects.Image)
    if (!this.inventoryBtn) {
      return;
    }

    // Check if inventoryBtn has a valid texture and scene reference
    if (!this.inventoryBtn.texture || !this.inventoryBtn.scene) {
      console.warn(
        "WindowManager.updateInventoryIcon: inventoryBtn texture or scene is invalid",
      );
      return;
    }

    let playerHasBackpack = false;
    if (
      window.player &&
      window.player.data &&
      typeof window.player.data.equipment === "object"
    ) {
      // Check if 'Back' key exists and is not null/undefined/empty
      const backItem = window.player.data.equipment["Back"];
      playerHasBackpack = !!backItem;
    }

    const newIconKey = playerHasBackpack ? "inventory-icon" : "belt-icon";

    // Safely check current texture key
    let currentTextureKey;
    try {
      currentTextureKey = this.inventoryBtn.texture.key;
    } catch (error) {
      console.warn(
        "WindowManager.updateInventoryIcon: Error accessing texture key:",
        error,
      );
      return;
    }

    // Additional safety checks before calling setTexture
    if (currentTextureKey !== newIconKey) {
      // Verify the scene is still valid and the texture exists
      if (!this.scene || !this.scene.sys || !this.scene.sys.textures) {
        console.warn(
          "WindowManager.updateInventoryIcon: scene became invalid during execution",
        );
        return;
      }

      // Check if the texture exists before setting it
      if (!this.scene.sys.textures.exists(newIconKey)) {
        console.warn(
          `WindowManager.updateInventoryIcon: texture '${newIconKey}' does not exist`,
        );
        return;
      }

      try {
        this.inventoryBtn.setTexture(newIconKey);
      } catch (error) {
        console.warn(
          "WindowManager.updateInventoryIcon: Error setting texture:",
          error,
        );
      }
    }
  }

  /**
   * Create and display three action slots at the center bottom of the screen using Phaser.
   */
  showActionSlots() {
    if (this.scene.scene.key === "BaseScene") return;
    if (this.actionUIForceHidden) return;
    if (!this.isTutorialVisible()) {
      if (!this.actionSlotsContainer) {
        const slotCount = 3;
        const slotSpacing = 90;
        this.actionSlots = [];
        this.actionSlotsContainer = this.scene.add.container(
          this.scene.scale.width / 2,
          this.scene.scale.height - 60,
        );
        this.uiLayer.add(this.actionSlotsContainer);
        for (let i = 0; i < slotCount; i++) {
          const x = (i - 1) * slotSpacing;
          const slot = new ActionSlot(this.scene, x, 0, null);
          this.actionSlots.push(slot);
          this.actionSlotsContainer.add(slot.container);
        }

        this.actionSlotsContainer.setScrollFactor(0);
        this.actionSlotsContainer.setDepth(10000); // Below open/shop buttons
      } else if (!this.actionSlotsInteractive) {
        // If container exists but was hidden, just show and re-enable it
        // But only if we're not currently hiding for tutorial
        this.uiLayer.add(this.actionSlotsContainer);
        this.actionSlotsContainer.setVisible(true);
        // Re-enable interactivity
        this.actionSlots.forEach((slot) => {
          if (
            slot.container &&
            slot.container.scene &&
            slot.container.scene.sys
          ) {
            slot.container.setInteractive({ useHandCursor: true });
          }
        });
        this.actionSlotsInteractive = true;
      }

      this.updateActionSlots();
    }
    showJoysticks();
  }

  hideActionSlots() {
    if (this.actionSlotsContainer) {
      this.actionSlotsContainer.setVisible(false);
      // Disable interactivity to prevent blocking pointer events
      this.actionSlots.forEach((slot) => {
        if (
          slot.container &&
          slot.container.scene &&
          slot.container.scene.sys
        ) {
          slot.container.disableInteractive();
        }
      });
      this.actionSlotsInteractive = false;
    }
    // Hide joysticks
    hideJoysticks();
  }

  public showRanksWindow(onClose?: () => void) {
    this.ranksWindow.show(onClose);
    this.updateActionSlotsVisibility();
  }

  public onSquadInfo(squadCode: string, members: string[] = [], memberDetails: SquadMemberDetail[] = []) {
    this.scene.events.emit("squad-info", { squadCode, members, memberDetails });
  }

  public onSquadLeft() {
    this.scene.events.emit("squad-left");
  }

  public onSquadError(message: string) {
    this.scene.events.emit("squad-error", { message });
  }

  public onSquadUpdate(data: SquadUpdateData) {
    this.scene.events.emit("squad-update", data);
  }

  public updateSquadIcon() {
    // BaseScene owns the visible squad button, so this is handled there via events.
  }

  public hideRanksWindow() {
    this.ranksWindow.hide();
    this.updateActionSlotsVisibility();
  }

  public isRanksWindowVisible(): boolean {
    return this.ranksWindow.isVisible();
  }

  /**
   * Show player info widget (ranks button)
   */
  showPlayerInfoWidget() {
    this.hidePlayerWidget();

    // BaseScene and MarketScene don't show the ranks button
    if (this.scene.scene.key === "BaseScene" || this.scene.scene.key === "MarketScene") return;

    const marginX = 16;
    const marginY = 16;
    const btnSize = 48;

    const ranksButtonX = marginX + btnSize / 2;
    const ranksButtonY = this.scene.scale.height - marginY - btnSize / 2;

    // Ranks button (leaderboard)
    if (
      !this.ranksButton ||
      !this.ranksButton.active ||
      this.ranksButton.scene === null
    ) {
      if (this.ranksButton && this.ranksButton.destroy) {
        this.ranksButton.destroy();
      }
      this.ranksButton = this.scene.add.image(ranksButtonX, ranksButtonY, "ranks-icon");
      this.ranksButton.setDisplaySize(btnSize, btnSize);
      this.ranksButton.setOrigin(0.5);
      this.ranksButton.setScrollFactor(0);
      this.ranksButton.setDepth(100011);
      this.ranksButton.setInteractive({ useHandCursor: true });
      this.ranksButton.on("pointerdown", () => {
        playSound(this.scene, "menu-click-sound", null, 0.5);
        this.ranksWindow.show();
        this.updateActionSlotsVisibility();
      });
      this.uiLayer.add(this.ranksButton);
    } else {
      this.ranksButton.setPosition(ranksButtonX, ranksButtonY);
      this.ranksButton.setVisible(true);
    }

    // Apply correct visibility based on scene / tutorial state
    this.updateActionSlotsVisibility();
  }

  /**
   * Hide player widget
   */
  hidePlayerWidget() {
    if (this.ranksButton) {
      this.ranksButton.destroy();
      this.ranksButton = null;
    }
  }

  isOpenButtonVisible() {
    return !!this.openButton;
  }

  getCurrentLootBox(): { x: number; y: number } | null {
    if (this.lootWindow) {
      return this.lootWindow.getCurrentLootBox();
    }
    return null;
  }

  showLootWindow(data: { x: number; y: number; items: Item[] }) {
    // Hide open button when showing loot window
    this.hideOpenButton();
    this.closeConflictingWindows("left");

    // Reset desktop key states when opening window
    resetDesktopKeyStates(this.scene);
    this.lootWindow.show(data);
    this.updateActionSlotsVisibility();
    this.updateCloseButtonVisibility();
  }

  showCosmeticsWindow() {
    this.hideOpenButton();
    // Close all windows (center position closes everything)
    this.closeConflictingWindows("center");
    // Reset desktop key states when opening window
    resetDesktopKeyStates(this.scene);
    this.cosmeticsWindow.show();
    this.updateActionSlotsVisibility();
    this.updateCloseButtonVisibility();
  }

  hideCosmeticsWindow() {
    this.cosmeticsWindow.hide();
    this.updateActionSlotsVisibility();
    this.updateCloseButtonVisibility();
  }

  isCosmeticsWindowVisible() {
    return this.cosmeticsWindow.isVisible();
  }


  hideLootWindow() {
    this.lootWindow.hide();
    this.updateActionSlotsVisibility();
    this.updateCloseButtonVisibility();
  }

  showMerchantWindow(
    items: (import("../models/common").ItemDefinition & { availableAt?: number })[],
    merchantId: number,
    merchantName: string,
    merchantLevel: number,
    fetchItems: { id: number; count?: number }[],
    fetchItemDefs: import("../models/common").ItemDefinition[],
    canUpgrade: boolean,
  ) {
    this.hideOpenButton();
    this.closeAllWindows();

    // Reset desktop key states when opening window
    resetDesktopKeyStates(this.scene);
    this.inventoryWindow.show();
    this.merchantWindow.show(items, merchantId, merchantName, merchantLevel, fetchItems, fetchItemDefs, canUpgrade);
    this.updateActionSlotsVisibility();
    this.updateCloseButtonVisibility();
  }

  hideMerchantWindow() {
    this.merchantWindow.hide();
    this.updateActionSlotsVisibility();
    this.updateCloseButtonVisibility();
  }

  isMerchantWindowVisible() {
    return this.merchantWindow.isVisible();
  }

  getMerchantWindowMerchantId() {
    return this.merchantWindow.getCurrentMerchantId();
  }

  closeLootWindowIfMatches(x: number, y: number) {
    if (this.lootWindow.isVisible()) {
      const currentLootBox = this.lootWindow.getCurrentLootBox();
      if (currentLootBox.x === x && currentLootBox.y === y) {
        this.lootWindow.hide();
        this.showOpenButton(this.scene);
        return true; // Indicates the window was closed
      }
    }
    return false;
  }

  showOpenButton(scene: Phaser.Scene, onOpen?: () => void, label?: string) {
    // Don't show if any window is open
    if (this.isAnyWindowOpen()) {
      return;
    }

    // Don't show if player is dead (only check if actually in MainScene)
    const isMainScene = scene.scene.key === "MainScene";
    if (isMainScene) {
      const mainScene = scene as any;
      if (mainScene && mainScene.playerDead) {
        return;
      }
    }

    // Remove previous button if exists
    if (this.openButton) {
      this.openButton.destroy();
    }

    // Button dimensions and position (use scale to ignore camera zoom)
    const btnWidth = 120;
    const btnHeight = 40;
    const x = scene.scale.width / 2;
    const y = this.ACTION_BUTTON_POS_Y;

    // Ensure i18next is ready and get correct button text
    const buttonText = this.getButtonText(label);

    // Create ModernButton with success style (green)
    this.openButton = new ModernButton({
      scene: scene,
      x: x,
      y: y,
      text: buttonText,
      style: "success",
      onClick: () => {
        // Play open-container sound for loot boxes
        playSound(scene, "open-container-sound", null, 0.5);
        this.hideOpenButton();
        if (onOpen) {
          onOpen();
        }
      },
    });

    this.uiLayer.add(this.openButton);
    this.openButton.setScrollFactor(0);
    this.openButton.setDepth(100000); // Ensure always on top
    this.uiLayer.bringToTop(this.openButton); // Bring to top within the container
    this.updateActionSlotsVisibility();
  }

  hideOpenButton() {
    if (this.openButton) {
      this.openButton.destroy();
      this.openButton = null;
    }
  }

  /**
   * Shows action buttons for loot interaction
   * For single items: shows PICK UP, USE (consumable), or EQUIP (equipment) buttons
   * For multiple items: shows OPEN button
   */
  showLootActionButtons(
    scene: Phaser.Scene,
    lootBox: LootBoxInfo,
    isFixed: boolean,
    onOpenLootBox?: () => void,
  ) {
    // Don't show if any window is open
    if (this.isAnyWindowOpen()) {
      return;
    }

    // Don't show if player is dead (only check if actually in MainScene)
    const isMainScene = scene.scene.key === "MainScene";
    if (isMainScene) {
      const mainScene = scene as any;
      if (mainScene && mainScene.playerDead) {
        return;
      }
    }

    const isSingleItem =
      !isFixed && !lootBox.isAirDrop && lootBox.items.length === 1;

    // Hide existing action buttons first
    this.hideLootActionButtons();

    if (isSingleItem) {
      // Handle single item case: show action buttons
      const item = lootBox.items[0];
      const btnSpacing = 12;
      let centerX = scene.scale.width / 2;

      // Create buttons array
      const buttons: ModernButton[] = [];

      // PICK UP button
      const pickUpButton = new ModernButton({
        scene: scene,
        x: 0, // Will be positioned after creation
        y: this.ACTION_BUTTON_POS_Y,
        text: this.t("ui:buttons.pickUp", "PICK UP"),
        style: "primary",
        onClick: () => {
          playSound(scene, "pick-item-sound", null, 0.5);
          if (typeof window !== "undefined" && (window as any).socket) {
            (window as any).socket.emit("pickup-item", {
              lootBoxX: lootBox.x,
              lootBoxY: lootBox.y,
              itemIdx: 0,
            });
          }
          this.hideLootActionButtons();
        },
      });
      buttons.push(pickUpButton);

      // USE button (only for consumable items)
      if (item && item.definition.type === "Consumable") {
        const useButton = new ModernButton({
          scene: scene,
          x: 0, // Will be positioned after creation
          y: this.ACTION_BUTTON_POS_Y,
          text: this.t("ui:buttons.use", "USE"),
          style: "success",
          onClick: () => {
            playSound(scene, "pick-item-sound", null, 0.5);
            if (typeof window !== "undefined" && (window as any).socket) {
              (window as any).socket.emit("use-item", {
                itemId: item.id,
                slotType: SlotType.LOOT,
              });
            }
            this.hideLootActionButtons();
          },
        });
        buttons.push(useButton);
      }

      // EQUIP button (only for equipment items)
      if (item && item.definition.type === "Equipment") {
        const equipButton = new ModernButton({
          scene: scene,
          x: 0, // Will be positioned after creation
          y: this.ACTION_BUTTON_POS_Y,
          text: this.t("ui:buttons.equip", "EQUIP"),
          style: "success",
          onClick: () => {
            playSound(scene, "pick-item-sound", null, 0.5);
            if (typeof window !== "undefined" && (window as any).socket) {
              if (item.definition.equipType === "Back") {
                if (window.player && window.player.data) {
                  window.player.data.equipment = window.player.data.equipment || {};
                  window.player.data.equipment.Back = item;
                }
                this.updateInventoryIcon();
              }
              (window as any).socket.emit("equip-item", {
                itemId: item.id,
                slotType: SlotType.LOOT,
                lootBoxX: lootBox.x,
                lootBoxY: lootBox.y,
              });
            }
            this.hideLootActionButtons();
          },
        });
        buttons.push(equipButton);
      }

      // Calculate total width needed
      let totalWidth = 0;
      buttons.forEach((btn) => {
        totalWidth += (btn as any).buttonWidth;
      });
      totalWidth += btnSpacing * (buttons.length - 1);

      // Position buttons horizontally centered
      let startX = centerX - totalWidth / 2;
      buttons.forEach((btn, idx) => {
        const btnWidth = (btn as any).buttonWidth;
        btn.setX(startX + btnWidth / 2);
        startX += btnWidth + btnSpacing;
      });

      // Create container and add to UI layer
      this.actionButtonsContainer = scene.add.container(0, 0, buttons);
      this.actionButtonsContainer.setDepth(100000);
      this.actionButtonsContainer.setScrollFactor(0);
      this.uiLayer.add(this.actionButtonsContainer);
    } else {
      // Handle multiple items case: show OPEN button (will use translation)
      this.hideOpenButton();
      this.showOpenButton(scene, onOpenLootBox);
    }
  }

  hideLootActionButtons() {
    if (this.actionButtonsContainer) {
      this.actionButtonsContainer.destroy();
      this.actionButtonsContainer = null;
    }
    this.hideMerchantButton();
    this.setActionSlotsVisible(true);
  }

  /**
   * Hide or show action slots (called when showing/hiding loot action buttons)
   */
  private setActionSlotsVisible(visible: boolean) {
    if (this.actionSlots) {
      this.actionSlots.forEach((slot) => {
        if (slot.container) {
          slot.container.setVisible(visible);
        }
      });
    }
  }

  showMerchantButton(scene: Phaser.Scene, merchant: Merchant) {
    // Don't show if any window is open
    if (this.isAnyWindowOpen()) {
      return;
    }

    // Hide existing merchant button first
    this.hideMerchantButton();

    const centerX = scene.scale.width / 2;
    
    // Ensure i18next is ready and get correct button text
    const tradeText = this.t("ui:buttons.trade", "TRADE");
    
    const tradeButton = new ModernButton({
      scene: scene,
      x: centerX,
      y: this.ACTION_BUTTON_POS_Y,
      text: tradeText,
      style: "success",
      onClick: () => {
        playSound(scene, "pick-item-sound", null, 0.5);
        this.hideMerchantButton();
        if (window.socket) {
          window.socket.emit("merchant-items", { merchantId: merchant.info.id });
        }
      },
    });

    this.merchantButton = tradeButton;
  }

  hideMerchantButton() {
    if (this.merchantButton) {
      this.merchantButton.destroy?.();
      this.merchantButton = null;
    }
  }

  showUIButtons(scene: Phaser.Scene) {
    // Remove previous UI buttons if exist
    if (this.uiBtnContainer) {
      this.uiBtnContainer.destroy();
      this.uiBtnContainer = null;
    }

    // Explicitly destroy individual button objects to prevent orphans
    if (this.inventoryBtn) {
      try {
        this.inventoryBtn.destroy();
      } catch (e) {
        console.warn("Error destroying inventoryBtn:", e);
      }
    }
    this.inventoryBtn = null;

    if (this.adminBtn) {
      try {
        this.adminBtn.destroy();
      } catch (e) {
        console.warn("Error destroying adminBtn:", e);
      }
    }
    this.adminBtn = null;

    if (this.adminText) {
      try {
        this.adminText.destroy();
      } catch (e) {
        console.warn("Error destroying adminText:", e);
      }
    }
    this.adminText = null;

    // Button size and spacing
    const btnSize = 48;
    const spacing = 8;
    const marginX = 16;
    const marginY = 16;
    const xStart = scene.scale.width - marginX * 2 - btnSize / 2;
    const yStart = marginY + btnSize / 2;

    // Create inventory button with correct icon based on current equipment
    // Determine which icon to use (backpack or belt)
    let playerHasBackpack = false;
    if (
      window.player &&
      window.player.data &&
      typeof window.player.data.equipment === "object"
    ) {
      const backItem = window.player.data.equipment["Back"];
      playerHasBackpack = !!backItem;
    }
    const iconKey = playerHasBackpack ? "inventory-icon" : "belt-icon";
    const isBaseScene = scene.scene.key === "BaseScene";

    if (!isBaseScene) {
      this.inventoryBtn = scene.add
        .image(0, 0, iconKey)
        .setDisplaySize(btnSize, btnSize);
      this.inventoryBtn.setOrigin(0.5);
      this.inventoryBtn.setScrollFactor(0);
      this.inventoryBtn.setDepth(100001);
      this.inventoryBtn.setInteractive({ useHandCursor: true });
      this.inventoryBtn.on("pointerdown", () => {
        if (this.inventoryWindow.isVisible()) {
          // Play close-container sound
          if (
            this.scene.sound &&
            this.scene.cache.audio.exists("close-container-sound")
          ) {
            playSound(this.scene, "close-container-sound", null, 0.5);
          }
          this.inventoryWindow.hide();
          this.updateActionSlotsVisibility();
          this.updateCloseButtonVisibility();
        } else {
          // Play open-container sound
          if (
            this.scene.sound &&
            this.scene.cache.audio.exists("inventory-open")
          ) {
            playSound(this.scene, "inventory-open", null, 0.5);
          }
          this.showInventoryWindow();
          this.updateActionSlotsVisibility();
        }
      });
    }

    // Create admin button (hidden by default, left of player button)
    const adminBtn = scene.add.rectangle(
      -2 * (btnSize + spacing),
      0,
      btnSize * 0.7,
      btnSize * 0.7,
      0x444444,
      0.8,
    );
    adminBtn.setOrigin(0.5);
    adminBtn.setScrollFactor(0);
    adminBtn.setDepth(100001);
    adminBtn.setStrokeStyle(4, 0xff0000); // Thick red border to make it visible
    adminBtn.setInteractive({ useHandCursor: true });
    adminBtn.setVisible(false); // Hidden by default

    // Add 'A' text to admin button
    const adminText = scene.add
      .text(-2 * (btnSize + spacing), 0, "A", {
        fontSize: "20px",
        color: "#ffffff",
        fontFamily: "Anton",
        fontStyle: "bold",
      })
      .setOrigin(0.5);
    adminText.setScrollFactor(0);
    adminText.setDepth(100002);
    adminText.setVisible(false); // Hidden by default

    adminBtn.on("pointerdown", () => {
      // Toggle admin mode
      if (typeof window !== "undefined" && (window as any).socket) {
        (window as any).socket.emit("toggle-admin-mode");
      }
    });

    const isMainScene =
      scene.scene.key === "MainScene" || scene.scene.key === "MarketScene";

    // Create player button using armor spritesheet frame 0,1 (first column, second row)
    // Frame index = (row * columns) + column = (1 * 10) + 0 = 10
    let playerBtn: Phaser.GameObjects.Image | null = null;
    if (!isBaseScene) {
      playerBtn = scene.add
        .image(-(btnSize + spacing), 0, "armor", 10)
        .setDisplaySize(btnSize, btnSize);
      playerBtn.setOrigin(0.5);
      playerBtn.setScrollFactor(0);
      playerBtn.setDepth(100001);
      playerBtn.setInteractive({ useHandCursor: true });
      playerBtn.on("pointerdown", () => {
        // Triple-tap opens debug console
        this.debugTapCount++;
        if (this.debugTapTimer) clearTimeout(this.debugTapTimer);
        if (this.debugTapCount >= 5) {
          this.debugTapCount = 0;
          this.debugTapTimer = null;
          this.showDebugWindow();
          return;
        }
        this.debugTapTimer = setTimeout(() => {
          this.debugTapCount = 0;
          this.debugTapTimer = null;
        }, 600);

        // Play menu-click sound
        if (
          this.scene.sound &&
          this.scene.cache.audio.exists("menu-click-sound")
        ) {
          playSound(this.scene, "menu-click-sound", null, 0.5);
        }
        // Toggle player window
        if (this.playerWindow.isVisible()) {
          this.playerWindow.hide();
          this.updateActionSlotsVisibility();
          this.updateCloseButtonVisibility();
        } else {
          this.showPlayerWindow();
          this.updateActionSlotsVisibility();
        }
      });
    }

    // Store admin button references
    this.adminBtn = adminBtn;
    this.adminText = adminText;

    // Container for all buttons
    const containerElements = [
      this.inventoryBtn,
      playerBtn,
      adminBtn,
      adminText,
    ].filter((el) => el !== null && el !== undefined) as Phaser.GameObjects.GameObject[];

    this.uiBtnContainer = scene.add.container(
      xStart,
      yStart,
      containerElements,
    );
    this.uiBtnContainer.setScrollFactor(0);
    this.uiBtnContainer.setDepth(100001);
    this.uiBtnContainer.setVisible(true);

    // Add to UI layer so it's only rendered by UI camera
    this.uiLayer.add(this.uiBtnContainer);

    // Update inventory icon to ensure it shows the correct one
    this.updateInventoryIcon();
    this.updateActionSlotsVisibility();
  }

  hideUIButtons() {
    if (this.uiBtnContainer) {
      this.uiBtnContainer.destroy();
      this.uiBtnContainer = null;
    }
    // Clear admin button references when hiding UI buttons
    this.adminBtn = null;
    this.adminText = null;
    this.inventoryBtn = null;
    this.updateActionSlotsVisibility();
  }

  showDebugWindow() {
    this.debugWindow.show();
  }

  hideDebugWindow() {
    this.debugWindow.hide();
  }

  showPlayerWindow() {
    this.closeConflictingWindows("left");

    // Reset desktop key states when opening window
    resetDesktopKeyStates(this.scene);
    if (this.playerWindow) {
      this.playerWindow.show();
    }
    this.updateActionSlotsVisibility();
    this.updateCloseButtonVisibility();
  }

  showInventoryWindow() {
    // Close conflicting right-side windows
    this.closeConflictingWindows("right");

    // Reset desktop key states when opening window
    resetDesktopKeyStates(this.scene);
    if (this.inventoryWindow) {
      this.inventoryWindow.show();
    }
    this.updateActionSlotsVisibility();
    this.updateCloseButtonVisibility();
  }

  hideInventoryWindow() {
    if (this.inventoryWindow) {
      this.inventoryWindow.hide();
    }
    this.updateCloseButtonVisibility();
  }

  /**
   * Closes windows based on position conflicts
   * - "left" closes other left windows
   * - "right" closes other right windows
   * - "center" closes all windows
   */
  private closeConflictingWindows(side: "left" | "right" | "center") {
    if (side === "center") {
      this.playerWindow?.hide();
      this.inventoryWindow?.hide();
      this.lootWindow?.hide();
      this.merchantWindow?.hide();
    } else if (side === "left") {
      this.playerWindow?.hide();
      this.lootWindow?.hide();
      this.merchantWindow?.hide();
    } else if (side === "right") {
      this.inventoryWindow?.hide();
    }
  }


  showAdminButton() {
    if (
      this.adminBtn &&
      this.adminText &&
      this.adminBtn.scene &&
      this.adminBtn.active
    ) {
      try {
        this.adminBtn.setVisible(true);
        this.adminText.setVisible(true);
      } catch (error) {
        console.warn("Error showing admin button:", error);
      }
    }
  }

  /**
   * Show the weapon UI in the upper left corner
   */
  showWeaponUI() {
    if (this.weaponUI) {
      // If weapon UI needs to be recreated, call show()
      if (!this.weaponUI.isVisible()) {
        this.weaponUI.show();
      } else {
        // Otherwise just make it visible
        this.weaponUI.setVisible(true);
      }
    }
  }

  /**
   * Hide the weapon UI
   */
  hideWeaponUI() {
    if (this.weaponUI) {
      this.weaponUI.setVisible(false);
    }
  }

  hideAdminButton() {
    if (
      this.adminBtn &&
      this.adminText &&
      this.adminBtn.scene &&
      this.adminBtn.active
    ) {
      try {
        this.adminBtn.setVisible(false);
        this.adminText.setVisible(false);
      } catch (error) {
        console.warn("Error hiding admin button:", error);
      }
    }
  }

  updateAdminButton(isAdmin: boolean) {
    if (
      this.adminBtn &&
      this.adminText &&
      this.adminBtn.scene &&
      this.adminBtn.active
    ) {
      try {
        const color = isAdmin ? 0x00ff00 : 0x444444;
        // Keep the thick red border to make it visible
        this.adminBtn.setFillStyle(color, 0.8);
        this.adminText.setColor(isAdmin ? "#ffffff" : "#cccccc");
      } catch (error) {
        console.warn("Error updating admin button:", error);
      }
    }
  }

  closeAllWindows() {
    if (this.lootWindow && this.lootWindow.isVisible()) {
      this.lootWindow.hide();
    }
    if (this.playerWindow && this.playerWindow.isVisible()) {
      this.playerWindow.hide();
    }
    if (this.inventoryWindow && this.inventoryWindow.isVisible()) {
      this.inventoryWindow.hide();
    }
    if (this.merchantWindow && this.merchantWindow.isVisible()) {
      this.merchantWindow.hide();
    }
    this.updateActionSlotsVisibility();
    this.updateCloseButtonVisibility();
  }

  hideAll() {
    this.lootWindow.hide();
    this.playerWindow.hide();
    this.inventoryWindow.hide();
    this.merchantWindow.hide();
    this.cosmeticsWindow.hide();
    this.ranksWindow.hide();
    this.hideOpenButton();
    this.hideUIButtons();
    this.hideWeaponUI();
    this.hideCloseWindowButton();
    this.actionSlotsContainer?.setVisible(false);
  }

  /**
   * Hide all UI elements for the daily reward screen
   * This includes player widget, action slots, and UI buttons
   */
  hideAllUIForReward() {
    // Hide all windows
    this.hideAll();
    this.hidePlayerWidget();

    if (this.dailyStarsComponent) {
      this.dailyStarsComponent.getContainer().setVisible(false);
    }

    // Hide weapon UI
    this.hideWeaponUI();
    // Make sure action slots are completely hidden
    if (this.actionSlotsContainer) {
      this.actionSlotsContainer.destroy();
      this.actionSlotsContainer = null;
    }
    // Clear the action slots array
    this.actionSlots = [];
  }

  showAllAfterReward() {
    if (this.ranksButton) {
      this.ranksButton.setVisible(true);
    }
    // Show weapon UI again
    this.showWeaponUI();
    // Recreate action slots
    this.showActionSlots();
    // Show UI buttons (inventory/player) again
    this.showUIButtons(this.scene);
    // Restore star meter visibility respecting scene/tutorial state
    this.updateActionSlotsVisibility();
  }

  destroy() {
    // Destroy buttons
    this.hideOpenButton();
    this.hideLootActionButtons();

    // Destroy player info
    this.hidePlayerWidget();

    // Destroy UI buttons container
    if (this.uiBtnContainer) {
      this.uiBtnContainer.destroy();
      this.uiBtnContainer = null;
    }

    // Destroy inventory button
    if (this.inventoryBtn) {
      this.inventoryBtn.destroy();
      this.inventoryBtn = null;
    }

    // Destroy admin button
    if (this.adminBtn) {
      this.adminBtn.destroy();
      this.adminBtn = null;
    }

    // Destroy admin text
    if (this.adminText) {
      this.adminText.destroy();
      this.adminText = null;
    }

    // Destroy close window button
    if (this.closeWindowButton) {
      this.closeWindowButton.destroy();
      this.closeWindowButton = null;
    }

    // Destroy action slots
    if (this.actionSlotsContainer) {
      this.actionSlotsContainer.destroy();
      this.actionSlotsContainer = null;
    }
    this.actionSlots = [];

    // Destroy bleeding icon
    if (this.bleedingIconObj) {
      this.bleedingIconObj.destroy();
      this.bleedingIconObj = null;
    }

    // Destroy windows by calling their destroy methods
    if (this.lootWindow) {
      this.lootWindow.destroy();
    }
    if (this.playerWindow) {
      this.playerWindow.destroy();
    }
    if (this.inventoryWindow) {
      this.inventoryWindow.destroy();
    }
    // Destroy utility components
    if (this.tutorialPopup) {
      this.tutorialPopup.destroy();
    }
    if (this.weaponUI) {
      this.weaponUI.destroy();
    }
    if (this.notificationHandler) {
      this.notificationHandler.destroy();
    }
  }

  hasActionSlots(): boolean {
    return this.actionSlotsContainer !== null;
  }

  updateActionSlots() {
    if (!this.actionSlotsContainer) return;
    if (typeof window === "undefined" || !(window as any).player) return;
    const player: Player = (window as any).player;
    const actions: Item[] = Array.isArray(player.data.actions)
      ? player.data.actions
      : [];

    for (let i = 0; i < this.actionSlots.length; i++) {
      const slot: ActionSlot = this.actionSlots[i];
      const item = actions[i] || null;
      slot.setItem(item);
    }
  }

  setActionUIForceHidden(hidden: boolean) {
    this.actionUIForceHidden = hidden;
    this.updateActionSlotsVisibility();
  }

  updateActionSlotsVisibility() {
    if (this.scene.scene.key === "BaseScene") return;
    if (!this.actionSlotsContainer) return;
    if (!this.scene || !this.scene.sys) return; // Safety check for scene
    const shouldBeVisible =
      !this.actionUIForceHidden && !this.isAnyWindowOpen() && !this.isTutorialVisible();

    // Hide action slots if any window is open or open button is visible
    this.actionSlotsContainer.setVisible(shouldBeVisible);

    if (shouldBeVisible) {
      showJoysticks();
    } else {
      hideJoysticks();
    }

    if (shouldBeVisible !== this.actionSlotsInteractive) {
      // CRITICAL: Only update interaction state when it changes to prevent blocking pointer events
      // In Phaser 3, invisible objects can still intercept pointer events!
      this.actionSlotsInteractive = shouldBeVisible;
      this.actionSlots.forEach((slot) => {
        if (
          slot.container &&
          slot.container.scene &&
          slot.container.scene.sys
        ) {
          if (shouldBeVisible) {
            slot.container.setInteractive({ useHandCursor: true });
          } else {
            slot.container.disableInteractive();
          }
        }
      });
    }

    if (this.dailyStarsComponent) {
      const isMarketScene = this.scene.scene.key === "MarketScene";
      if (this.isBaseScene && shouldBeVisible && !isMarketScene) {
        this.dailyStarsComponent.getContainer().setVisible(true);
      } else {
        this.dailyStarsComponent.getContainer().setVisible(false);
      }
    }
  }

  /**
   * Show the centralized close button at the bottom middle of the screen
   */
  private showCloseWindowButton() {
    // Remove previous button if exists
    if (this.closeWindowButton) {
      this.closeWindowButton.destroy();
      this.closeWindowButton = null;
    }

    // Ensure OPEN button is hidden to prevent blocking
    this.hideOpenButton();

    // Match the OPEN button positioning
    const btnHeight = 40;
    const x = this.scene.scale.width / 2;
    const y = this.scene.scale.height - btnHeight / 2 - 24;

    this.closeWindowButton = new ModernButton({
      scene: this.scene,
      x: x,
      y: y,
      text: "",
      style: "danger",
      iconOnly: true,
      icon: "close-icon",
      onClick: () => {
        // Play close-container sound
        if (
          this.scene.sound &&
          this.scene.cache.audio.exists("close-container-sound")
        ) {
          playSound(this.scene, "close-container-sound", null, 0.5);
        }
        this.closeAllWindows();
      },
    });

    this.closeWindowButton.setScale(1.5); // Make the button 1.5x bigger
    this.closeWindowButton.setScrollFactor(0);
    this.closeWindowButton.setDepth(300000); // Ensure it's above all windows (highest window is 200000)
    this.uiLayer.add(this.closeWindowButton);
    this.uiLayer.bringToTop(this.closeWindowButton); // Ensure it's on top within the container
  }

  /**
   * Hide the centralized close button
   */
  private hideCloseWindowButton() {
    if (this.closeWindowButton) {
      this.closeWindowButton.destroy();
      this.closeWindowButton = null;
    }
  }

  /**
   * Update the close button visibility based on whether any window is open
   * This method is public so it can be called from BaseWindow.hide()
   */
  updateCloseButtonVisibility() {
    // In the hub BaseScene windows are always-visible panels — never show a floating close button
    if (this.scene.scene.key === "BaseScene") {
      this.hideCloseWindowButton();
      return;
    }
    const anyWindowOpen = this.isAnyWindowOpen();
    const isInputWindowOpen = false;
    const buttonExists = !!this.closeWindowButton;

    // Hide close button if input window is open (since it has its own cancel button)
    if (isInputWindowOpen && buttonExists) {
      this.hideCloseWindowButton();
    }
    // Only show if windows are open AND button doesn't exist AND input window is not open
    else if (anyWindowOpen && !buttonExists && !isInputWindowOpen) {
      this.showCloseWindowButton();
    }
    // If windows are open and button exists, bring it to top (only if input window is not open)
    else if (
      anyWindowOpen &&
      buttonExists &&
      this.closeWindowButton &&
      !isInputWindowOpen
    ) {
      this.uiLayer.bringToTop(this.closeWindowButton);
    }
    // Only hide if no windows are open AND button exists
    else if (!anyWindowOpen && buttonExists) {
      this.hideCloseWindowButton();
    }
    // Otherwise, do nothing to prevent infinite loop
  }

  /**
   * Call this in your main scene's update loop to show/hide the bleeding icon as needed.
   */
  updateBleedingIcon() {
    // Update weapon UI first
    this.weaponUI.refresh();

    // Check for player limb bleeding status
    let isBleeding = false;
    if (
      typeof window !== "undefined" &&
      (window as any).player &&
      (window as any).player.data &&
      (window as any).player.data.limbs
    ) {
      const limbStatus = (window as any).player.data.limbs;
      for (const limb in limbStatus) {
        if (limbStatus[limb] === "BLEEDING") {
          isBleeding = true;
          break;
        }
      }
    }
    if (isBleeding) {
      if (!this.bleedingIconObj) {
        // Position bleeding icon based on whether weapon UI is visible
        const weaponUIWidth = this.weaponUI.getUIWidth();
        const iconX = weaponUIWidth > 0 ? 20 + weaponUIWidth + 16 : 40; // Next to weapon UI or default position
        const iconY = 44; // Align with weapon UI center

        this.bleedingIconObj = this.scene.add.image(
          iconX,
          iconY,
          BLEEDING_ICON_KEY,
        );
        this.bleedingIconObj.setOrigin(0.5);
        this.bleedingIconObj.setDisplaySize(48, 48);
        this.bleedingIconObj.setScrollFactor(0);
        this.bleedingIconObj.setDepth(100010);
        // Add to UI layer so main camera ignores it
        this.uiLayer.add(this.bleedingIconObj);
      } else {
        // Update bleeding icon position in case weapon UI visibility changed
        const weaponUIWidth = this.weaponUI.getUIWidth();
        const iconX = weaponUIWidth > 0 ? 20 + weaponUIWidth + 16 : 40;
        this.bleedingIconObj.setX(iconX);
        this.bleedingIconObj.setVisible(true);
      }
    } else {
      if (this.bleedingIconObj) {
        this.bleedingIconObj.setVisible(false);
      }
    }
  }

  isInventoryWindowVisible(): boolean {
    return this.inventoryWindow && this.inventoryWindow.isVisible();
  }

  isLootWindowVisible(): boolean {
    return this.lootWindow && this.lootWindow.isVisible();
  }

  isPlayerWindowVisible(): boolean {
    return this.playerWindow && this.playerWindow.isVisible();
  }

  isAnyWindowOpen(): boolean {
    return (
      this.isLootWindowVisible() ||
      this.isPlayerWindowVisible() ||
      this.isInventoryWindowVisible() ||
      this.isMerchantWindowVisible()
    );
  }

  /**
   * Show a tutorial popup
   */
  showTutorial(
    tutorialName: string,
    tutorialData: { text: string[]; arrow?: any },
    onComplete?: () => void,
  ) {
    this.tutorialPopup.show(tutorialName, tutorialData, onComplete);
    this.hideOpenButton();
    this.updateActionSlotsVisibility();
  }

  /**
   * Hide the tutorial popup
   */
  hideTutorial(hideArrow: boolean = false) {
    this.tutorialPopup.hide(hideArrow);
    this.updateActionSlotsVisibility();
  }

  /**
   * Check if tutorial is currently visible
   */
  isTutorialVisible(): boolean {
    return this.tutorialPopup.isVisible();
  }

  getVisibleTutorialName(): string | null {
    return this.tutorialPopup.getActiveTutorial();
  }

  /**
   * Completely destroy the tutorial (including arrow)
   */
  destroyTutorial() {
    this.tutorialPopup.destroy();
  }

  public showNotification(message: string, type: NotificationType, label?: string) {
    this.notificationHandler.show(message, type, undefined, label);
  }

  public getLootWindow(): LootWindow {
    return this.lootWindow;
  }
}
